import axios, { type AxiosError } from 'axios'
import {
    useQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { useAtom } from "jotai"

import type {
    FormMutationOptions,
    MunicipalityAwardFormInput,
    MunicipalityAward,
} from '@/types'
import { selectedMunicipalityIdAtom } from "@/state"
import { isErrorResponse } from '@/helpers'

export function useMunicipalityAwards(
    municipalityId: number,
) {
    return useQuery({
        queryKey: ['municipalityAwards', municipalityId],
        queryFn: async () => {
            const { data } : { data: Array<MunicipalityAward>} = await axios.get(
                `/api/municipality_award/list?municipality_id=${municipalityId}`
            )
            return data
        },
    })
}

export function useMunicipalityAward(municipalityAwardId) {
    return useQuery({
        queryKey: ['municipalityAward', municipalityAwardId],
        queryFn: async () => {
            const { data } : { data: MunicipalityAward } = await axios.get(
                '/api/municipality_award/single?id=' + municipalityAwardId,
            )
            return data
        },
    })
}

export const useCreateMunicipalityAward = (options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const [municipalityId] = useAtom(selectedMunicipalityIdAtom)

    const {
        onSuccessCallback,
        onErrorCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: MunicipalityAwardFormInput) => {  
            const queryParams = new URLSearchParams()
            queryParams.append('municipality_id', municipalityId.toString())
            queryParams.append('title', data.title)
            if (data.year) {
                queryParams.append('year', data.year.toString())
            }
            if (data.description) {
                queryParams.append('description', data.description)
            }

            return axios.post(`/api/municipality_award/create?${queryParams.toString()}`
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['municipalityAwards'] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useUpdateMunicipalityAward = (municipalityAwardId, options: FormMutationOptions) => {
    const queryClient = useQueryClient()

    const {
        onSuccessCallback,
        onErrorCallback,
        formDataId,
        setRequestInProgress,
    } = options

    return useMutation({
        mutationFn: (data: MunicipalityAwardFormInput) => {
            return axios.put(`/api/municipality_award/update?id=${municipalityAwardId 
                }&title=${data.title    
                }&year=${data.year
                }&description=${data.description}`
            )
        },
        onSuccess: response => {
            queryClient.invalidateQueries({ queryKey: ['municipalityAwards'] })
            queryClient.invalidateQueries({ queryKey: ['municipalityAward-' + municipalityAwardId] })
            localStorage.removeItem(formDataId)
            if (onSuccessCallback) {
                onSuccessCallback(response.data)
            }
        },
        onSettled: () => {
            setRequestInProgress(false)
        },
        onError: (axiosResponse: AxiosError) => {
            const errorData = axiosResponse.response?.data
            if (onErrorCallback){
                if (isErrorResponse(errorData)) {
                    onErrorCallback(errorData.error)
                } else {
                    console.error(axiosResponse)
                    onErrorCallback('Došlo k neočekávané chybě.');
                }
            }
        }
    })
}

export const useDeleteMunicipalityAward = (municipalityAwardId, municipalityId) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => {
            return axios.delete(`/api/municipality_award/delete?id=${municipalityAwardId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['municipalityAwards', municipalityId] })
        },
    })
}

